import SearchIcon from '@mui/icons-material/Search';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Container,
    FormControlLabel,
    Grid,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import PageTitle from '../components/PageTitle';
import { GeneralLayout } from '../layouts/GeneralLayout';
import FileUploadButton from '../sections/@dashboard/common/FileUploadButton';
import {
    useGetRegistryDocumentQuery,
    useSaveDocumentToRegistryMutation,
} from '../store/document/documentApi';
import { useGetStudentsByPatternQuery } from '../store/student/studentApi';

const emptyDocument = {
    documentNumber: '',
    documentDate: '',
    fileGuid: null,
    studentId: '',
    semesters: [],
};

export default function CreateDocumentInRegistryPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const documentId = Number(id);
    const isEditing = Number.isInteger(documentId) && documentId > 0;
    const [document, setDocument] = useState(emptyDocument);
    const [existingStudentName, setExistingStudentName] = useState('');
    const [studentPattern, setStudentPattern] = useState('');
    const [submittedPattern, setSubmittedPattern] = useState('');
    const [saveDocument, { isLoading: isSaving }] = useSaveDocumentToRegistryMutation();
    const {
        data: existingDocument,
        isLoading: isDocumentLoading,
        isError: isDocumentLoadingFailed,
    } = useGetRegistryDocumentQuery(
        { documentId },
        { skip: !isEditing }
    );
    const { data: availableStudents, isFetching: areStudentsLoading } = useGetStudentsByPatternQuery(
        { pattern: submittedPattern },
        { skip: !submittedPattern }
    );

    useEffect(() => {
        if (!existingDocument) {
            return;
        }

        setDocument({
            documentNumber: existingDocument.documentNumber ?? '',
            documentDate: existingDocument.documentDate?.slice(0, 10) ?? '',
            fileGuid: existingDocument.registryDocumentGuid ?? null,
            studentId: existingDocument.studentId,
            semesters: [...(existingDocument.semesters ?? [])],
        });
        setExistingStudentName(existingDocument.studentFullName ?? '');
    }, [existingDocument]);

    const studentOptions = useMemo(() => {
        const students = availableStudents?.students ? [...availableStudents.students] : [];
        if (document.studentId && !students.some((student) => student.studentId === document.studentId)) {
            students.unshift({
                studentId: document.studentId,
                lastName: existingStudentName,
                firstName: '',
                middleName: '',
                groupName: '',
                isFired: false,
            });
        }
        return students;
    }, [availableStudents, document.studentId, existingStudentName]);

    function updateDocument(field, value) {
        setDocument((current) => ({ ...current, [field]: value }));
    }

    function toggleSemester(semester, checked) {
        setDocument((current) => ({
            ...current,
            semesters: checked
                ? [...current.semesters, semester].sort((left, right) => left - right)
                : current.semesters.filter((item) => item !== semester),
        }));
    }

    function renderSemesterCheckboxes(from, to) {
        return Array.from({ length: to - from + 1 }, (_, index) => from + index).map((semester) => (
            <FormControlLabel
                key={semester}
                sx={{ marginLeft: 0 }}
                control={(
                    <Checkbox
                        checked={document.semesters.includes(semester)}
                        onChange={(event) => toggleSemester(semester, event.target.checked)}
                    />
                )}
                label={`${semester}-й`}
            />
        ));
    }

    function findStudents() {
        const pattern = studentPattern.trim();
        if (pattern) {
            setSubmittedPattern(pattern);
        }
    }

    function canSave() {
        return Boolean(
            document.documentNumber.trim()
            && document.documentDate
            && document.fileGuid
            && document.studentId
            && document.semesters.length
        );
    }

    async function handleSave() {
        try {
            const response = await saveDocument({
                data: {
                    ...document,
                    documentId: isEditing ? documentId : null,
                },
            }).unwrap();

            if (response?.success) {
                navigate('/registry-of-documents');
                return;
            }

            alert(response?.errors?.join('\n') ?? 'Не удалось сохранить документ');
        } catch (error) {
            alert(error?.data?.errors?.join('\n') ?? 'Не удалось сохранить документ');
        }
    }

    const title = isEditing ? 'Редактировать документ' : 'Создать документ';

    return (
        <GeneralLayout>
            <PageTitle title={title} />

            <Container>
                <Stack direction="column" padding="120px 0 0 100px" spacing={2}>
                    <Typography variant="h4" gutterBottom>
                        {title}
                    </Typography>

                    {isDocumentLoadingFailed && (
                        <Alert severity="error">Не удалось загрузить документ для редактирования</Alert>
                    )}

                    <Card>
                        <CardContent>
                            {isDocumentLoading ? (
                                <Stack alignItems="center" padding={3}>
                                    <CircularProgress />
                                </Stack>
                            ) : (
                                <Stack spacing={2}>
                                    <Grid container columnSpacing={3}>
                                        <Grid item xs={5}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Stack spacing={2}>
                                                        <Stack spacing={1}>
                                                            <Typography><strong>Номер документа</strong></Typography>
                                                            <TextField
                                                                size="small"
                                                                value={document.documentNumber}
                                                                onChange={(event) => updateDocument(
                                                                    'documentNumber', event.target.value
                                                                )}
                                                            />
                                                        </Stack>

                                                        <Stack spacing={1}>
                                                            <Typography><strong>Дата документа</strong></Typography>
                                                            <TextField
                                                                size="small"
                                                                type="date"
                                                                value={document.documentDate}
                                                                onChange={(event) => updateDocument(
                                                                    'documentDate', event.target.value
                                                                )}
                                                            />
                                                        </Stack>

                                                        <Stack spacing={1}>
                                                            <Typography><strong>Файл</strong></Typography>
                                                            <FileUploadButton
                                                                maxFileSize={2.5 * 1024 * 1024}
                                                                title="Выбор файла"
                                                                align="left"
                                                                uploadingEnabled
                                                                existingFileGuid={document.fileGuid}
                                                                onFileUploaded={(url, guid) => updateDocument(
                                                                    'fileGuid', guid
                                                                )}
                                                                onFileDelete={() => updateDocument('fileGuid', null)}
                                                            />
                                                        </Stack>

                                                        <Stack spacing={1}>
                                                            <Typography><strong>Студент (поиск по ФИО)</strong></Typography>
                                                            <TextField
                                                                size="small"
                                                                value={studentPattern}
                                                                onChange={(event) => setStudentPattern(event.target.value)}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        findStudents();
                                                                    }
                                                                }}
                                                            />
                                                            <Button
                                                                variant="contained"
                                                                disabled={!studentPattern.trim() || areStudentsLoading}
                                                                startIcon={areStudentsLoading
                                                                    ? <CircularProgress size={15} />
                                                                    : <SearchIcon />}
                                                                onClick={() => findStudents()}
                                                            >
                                                                Искать
                                                            </Button>
                                                        </Stack>

                                                        <Stack spacing={1}>
                                                            <Typography>
                                                                <strong>Студент</strong>
                                                                {availableStudents?.students
                                                                    ? ` (${availableStudents.students.length} найдено)`
                                                                    : ''}
                                                            </Typography>
                                                            <Select
                                                                size="small"
                                                                value={document.studentId}
                                                                onChange={(event) => updateDocument(
                                                                    'studentId', event.target.value
                                                                )}
                                                            >
                                                                {studentOptions.map((student) => (
                                                                    <MenuItem key={student.studentId} value={student.studentId}>
                                                                        {[
                                                                            student.lastName,
                                                                            student.firstName,
                                                                            student.middleName,
                                                                        ].filter(Boolean).join(' ')}
                                                                        {student.groupName ? ` (${student.groupName})` : ''}
                                                                        {student.isFired ? ' — отчислен(а)' : ''}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </Stack>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        <Grid item xs={7}>
                                            <Stack spacing={1}>
                                                <Typography><strong>Семестры</strong></Typography>
                                                <Stack direction="row" spacing={3}>
                                                    <Stack>{renderSemesterCheckboxes(1, 6)}</Stack>
                                                    <Stack>{renderSemesterCheckboxes(7, 11)}</Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    </Grid>

                                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                        <Button onClick={() => navigate('/registry-of-documents')}>
                                            Отмена
                                        </Button>
                                        <Button
                                            variant="contained"
                                            disabled={!canSave() || isSaving || isDocumentLoadingFailed}
                                            startIcon={isSaving ? <CircularProgress size={15} /> : null}
                                            onClick={() => handleSave()}
                                        >
                                            Сохранить
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Stack>
            </Container>
        </GeneralLayout>
    );
}
